import { Card } from '@platform/components';
import { RepCard, RepProjectPanelProject } from 'lib/client/src/platformapi.schemas';
import dataTestids from '../../constants/test-ids';
import { getHeaderContent, getButtonProps, getCardStatusBadge, getSubtext } from './utils';
import { useSearch } from 'wouter';
import { useEffect, useContext } from 'react';
import { IntroductionModalContext } from './Modals/IntroductionModal';
import { DiscoveryCallUpdateModalContext } from './Modals/DiscoveryCallUpdateModal';
import { UpdateProjectDatesModalContext } from './Modals/UpdateProjectDatesModal';

export interface RepUserCardProps {
  cardData: RepCard;
  project?: RepProjectPanelProject;
}

export const RepUserCard = (props: RepUserCardProps) => {
  const { cardData, project } = {
    ...props,
  };

  const { openIntroductionModal } = useContext(IntroductionModalContext);
  const { openDiscoveryCallUpdateModal } = useContext(DiscoveryCallUpdateModalContext);
  const { openUpdateProjectDatesModal } = useContext(UpdateProjectDatesModalContext);

  // Open modal if introductionId is in the URL param
  const searchParams = useSearch();
  const urlSearchParams = new URLSearchParams(searchParams);
  const paramIntroId = parseInt(urlSearchParams.get('introductionId') ?? '');
  useEffect(() => {
    if (paramIntroId && paramIntroId === cardData.introduction.id) {
      openIntroductionModal({
        userType: 'organization',
        cardData: cardData,
        project: project,
      });
    }
  }, [paramIntroId, cardData, openIntroductionModal, project]);

  const { professional, avatar } = { ...cardData };

  const proName = professional.firstname + ' ' + professional.lastname;

  const headerContentProps = getHeaderContent({ cardData, userType: 'rep' });
  const subtext = getSubtext({ cardData, userType: 'rep' });
  const statusBadge = getCardStatusBadge(cardData.cardStatus);
  const buttonProps = getButtonProps({ cardData, userType: 'rep' });
  let buttonCta: (() => void) | undefined = undefined;
  switch (buttonProps?.modal) {
    case 'introduction':
      buttonCta = () => {
        openIntroductionModal({
          userType: 'organization',
          cardData: cardData,
          project: project,
        });
      };
      break;
    case 'discovery':
      buttonCta = () => {
        openDiscoveryCallUpdateModal({
          userType: 'organization',
          cardData: cardData,
          project: project,
        });
      };
      break;
    case 'projectDates':
      buttonCta = () => {
        if (project) {
          openUpdateProjectDatesModal({
            userType: 'organization',
            isTeam: false,
            id: project.id,
            startDate: new Date(project.startDate ?? new Date()),
            endDate: new Date(project.endDate ?? new Date()),
            professionalFirstName: proName,
          });
        }
      };
      break;
    case 'email':
      buttonCta = () => {
        window.open(
          `mailto:match@movingworlds.org?subject=Update on match - project #${project?.id}`,
        );
      };
      break;
    default:
      buttonCta = undefined;
      break;
  }

  const headerContent = <div className='font-medium'>{headerContentProps.headerText}</div>;

  return (
    <Card
      headerContent={headerContent}
      headerColor={headerContentProps.headerColor}
      buttonText={buttonProps?.buttonText}
      buttonVariant={buttonProps?.buttonVariant}
      content={proName}
      contentTextHref={
        professional.id ?
          `${import.meta.env['VITE_LEGACY_APP_URL']}/professionals/${professional.id}`
        : undefined
      }
      avatarSize='md'
      avatarHref={avatar || ''}
      contentSubText={subtext}
      badges={[statusBadge]}
      variant='small'
      buttonOnClick={buttonCta}
      dataTestId={`${dataTestids.MY_PROJECTS.PROJECT_CARD.CONTAINER}-${project?.id ?? 'custom'}-${professional.id}`}
    />
  );
};
